import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'

import {
  Divider,
  Icon,
  InfoOutlined,
  Tooltip,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, object, shape, string } from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { getPluginKey } from 'Pods/ThreeDStudio/Shared/Utils/dccPluginKeys'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { probeDccConnectivity } from 'Pods/ThreeDStudio/Store/actions/dccBridge'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

import DccBridgeRow from './Components/DccBridgeRow/DccBridgeRow'

function DccBridgePopover({
  softwares,
  plugins = EMPTY_OBJECT,
  selectedName = null,
  isLocked = false,
  onClosePopover,
}) {
  const t = useTranslation()
  const [openSubmenuName, setOpenSubmenuName] = useState(null)
  const connectedSoftwares = useThreeDStudioStore(state => state.dccBridge.connectedSoftwares)

  const { available, comingSoon } = useMemo(() => ({
    available: softwares.filter(s => s.isEnabled),
    comingSoon: softwares.filter(s => !s.isEnabled),
  }), [softwares])

  // Probe the shared local plugin port once per popover open: a single /health
  // round-trip identifies which plugin (if any) is running and classifies every
  // enabled row. To re-check, the user closes and reopens the dropdown, which
  // remounts this effect. Locked (non-subscriber) users cannot connect a plugin,
  // so the probe is skipped for them.
  useEffect(() => {
    if (isLocked) return

    probeDccConnectivity(available.map(s => s.name))
  }, [available, isLocked])

  const handleSubmenuOpenChange = useCallback((name, open) => {
    setOpenSubmenuName(prev => {
      if (open) return name
      return prev === name ? null : prev
    })
  }, [])

  // Click anywhere in the popover that isn't a three-dot icon (those stop
  // propagation) closes the open submenu. Outside clicks close the whole
  // popover via the parent ClosingDropdown, which unmounts this component
  // anyway.
  const handlePopoverClick = useCallback(() => {
    setOpenSubmenuName(null)
  }, [])

  return (
    <div className="dcc-bridge-popover" onClick={handlePopoverClick} role="presentation">
      <header className="dcc-bridge-popover__header">
        <span className="cgt-body-md-semibold">DCC Bridge</span>
        <Tooltip
          id="dcc-bridge-info-tooltip"
          place="top"
          content={t('footerActions.dccBridge.tooltip')}
        >
          <Icon
            svgComponent={InfoOutlined}
            color="dark"
            size="small"
            notSpaced
          />
        </Tooltip>
      </header>
      {available.length > 0 && (
        <>
          <Divider customClass="dcc-bridge-popover__divider" />
          <ul className="dcc-bridge-popover__list">
            {available.map(software => (
              <DccBridgeRow
                key={software.name}
                software={software}
                pluginKey={getPluginKey(software.name)}
                pluginVersion={plugins[getPluginKey(software.name)]}
                isSelected={software.name === selectedName}
                isSubmenuOpen={openSubmenuName === software.name}
                isConnected={Boolean(connectedSoftwares[software.name])}
                isLocked={isLocked}
                onSubmenuOpenChange={handleSubmenuOpenChange}
                onClosePopover={onClosePopover}
              />
            ))}
          </ul>
        </>
      )}
      {comingSoon.length > 0 && (
        <>
          <Divider customClass="dcc-bridge-popover__divider" />
          <div className="dcc-bridge-popover__caption cgt-caption-semibold">
            {t('footerActions.dccBridge.comingSoon')}
          </div>
          <ul className="dcc-bridge-popover__list">
            {comingSoon.map(software => (
              <DccBridgeRow
                key={software.name}
                software={software}
                pluginKey={getPluginKey(software.name)}
                pluginVersion={plugins[getPluginKey(software.name)]}
                isSubmenuOpen={openSubmenuName === software.name}
                onSubmenuOpenChange={handleSubmenuOpenChange}
                onClosePopover={onClosePopover}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

DccBridgePopover.propTypes = {
  softwares: arrayOf(shape({
    name: string.isRequired,
    iconName: string.isRequired,
    isEnabled: bool.isRequired,
  })).isRequired,
  plugins: object,
  selectedName: string,
  isLocked: bool,
  onClosePopover: func.isRequired,
}

export default memo(DccBridgePopover)
